import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { openNotificationSystemSettings } from '@/lib/notifications'

interface IosNotificationSettingsDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Shown after the user denies the iOS notification permission (or it was already denied).
 * Explains why notifications matter; "Open Settings" jumps to the system notification page.
 */
export function IosNotificationSettingsDialog({
  isOpen,
  onOpenChange,
}: IosNotificationSettingsDialogProps) {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || !isOpen) return null
  if (typeof document === 'undefined' || !document.body) return null

  const title = t('settings.notifications', 'Notifications')
  const body = t(
    'settings.notificationsDeniedDialogBody',
    'Turn on notifications to get alerts about new messages from artists. You can enable them in your device settings.',
  )
  const cta = t('settings.notificationsDeniedOpenSettings', 'Open Settings')

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/50 animate-in fade-in-0"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />

      <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none p-4">
        <div
          className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ios-notification-dialog-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 space-y-4 text-center">
            <h2 id="ios-notification-dialog-title" className="text-xl font-semibold text-foreground">
              {title}
            </h2>

            <p className="text-base text-foreground leading-relaxed">{body}</p>

            <button
              type="button"
              className="w-full mt-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 rounded-lg transition-colors"
              onClick={() => {
                void openNotificationSystemSettings()
                onOpenChange(false)
              }}
            >
              {cta}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
